// @flow
import * as Immutable from 'immutable'; // This is required
import ImmutableModel from 'common/models/flow/ImmutableModel'; // Make sure you copied this file into your repo

import { AuditMetadata } from 'common/models/flow/AuditMetadata';
import { LocationAddress } from 'common/models/flow/LocationAddress';
import { TeamId } from 'common/models/flow/TeamId';
import { LatLng } from 'common/models/flow/LatLng';
import { Volume } from 'common/models/flow/Volume';
import { Weight } from 'common/models/flow/Weight';
import { PickupContactPoint } from 'common/models/flow/PickupContactPoint';
import { AmountTransaction } from 'common/models/flow/AmountTransaction';
import { TimeSlot } from 'common/models/flow/TimeSlot';

import type { AuditMetadataModelType } from 'common/models/flow/AuditMetadata';
import type { TeamIdModelType } from 'common/models/flow/TeamId';
import type { LocationAddressModelType } from 'common/models/flow/LocationAddress';
import type { LatLngModelType } from 'common/models/flow/LatLng';
import type { TimeSlotModelType } from 'common/models/flow/TimeSlot';
import type { VolumeModelType } from 'common/models/flow/Volume';
import type { WeightModelType } from 'common/models/flow/Weight';
import type { QuantityModelType } from 'common/models/flow/Quantity';
import type { PickupContactPointModelType } from 'common/models/flow/PickupContactPoint';
import type { AmountTransactionModelType } from 'common/models/flow/AmountTransaction';
import type { AmountModelType } from 'common/models/flow/Amount';
import type { LineItemPartModelType } from 'common/models/flow/LineItemPart';

export type EntityStatusEnum = 'ACTIVE' | 'INACTIVE';
export type OrderStatusEnum =
    | 'OPEN'
    | 'PENDING_PLANNING'
    | 'PLANNING'
    | 'WAITING'
    | 'ON_GOING'
    | 'COMPLETED'
    | 'CANCELLED';
export type EffectiveStatusEnum =
    | 'OPEN'
    | 'PLANNING'
    | 'EXECUTION'
    | 'COMPLETED'
    | 'CANCELLED';

type TemperatureType = 'CHILLED' | 'FROZEN';
type TemperatureUnitType = 'C' | 'F';

export type TemperatureThresholdModelType = {
    lowerThreshold: number,
    higherThreshold: number,
    temperatureType: TemperatureType,
    unit: TemperatureUnitType
};

export type PreferredQuoteModelType = {
    carrier: string,
    totalFee: {
        amount: number,
        currency: string,
        estimatedDropOffTime: ?string
    },
    expiresAt: string
};

export type LineItemModelType = {
    id: string,
    lineItemId: string,
    name: string,
    parts: Array<LineItemPartModelType>,
    price: ?AmountModelType,
    quantity: number,
    quantityUnit: ?string
};

export type MasterOrderModelType = {
    clientId: string,
    id: string,
    code: string,
    name: string,
    status: EntityStatusEnum,
    orderStatus: OrderStatusEnum,
    effectiveStatus: EffectiveStatusEnum,
    version: number,
    batchId: string,
    planId: string,
    tourId: string,
    shiftId: string,
    scanId: string,
    sequence: number,
    userId: string,
    homebaseId: string,
    customProperties: { [key: string]: string },
    auditMetadata: ?AuditMetadataModelType,
    team: TeamIdModelType,
    teamId: string,
    skills: Array<string>,
    orderedOn: string,
    temperatureThreshold: ?TemperatureThresholdModelType,
    pickupVisitName: string,
    pickupLocationId: string,
    pickupContactPoint: ?PickupContactPointModelType,
    pickupLocationAddress: LocationAddressModelType,
    pickupLatLng: LatLngModelType,
    pickupDate: string,
    pickupSlot: TimeSlotModelType,
    pickupSlots: Array<TimeSlotModelType>,
    pickupTransactionDuration: number,
    pickupAmount: AmountTransactionModelType,
    pickupCustomerId: string,
    pickupAddressId: string,
    dropVisitName: string,
    dropLocationId: string,
    dropContactPoint: ?PickupContactPointModelType,
    dropLocationAddress: LocationAddressModelType,
    dropLatLng: LatLngModelType,
    dropDate: string,
    dropSlot: TimeSlotModelType,
    dropSlots: Array<TimeSlotModelType>,
    dropTransactionDuration: number,
    dropAmount: AmountTransactionModelType,
    dropCustomerId: string,
    dropAddressId: string,
    volume: VolumeModelType,
    weight: WeightModelType,
    quantity: ?QuantityModelType,
    usecase: ?('DISPATCH_IQ' | 'ORCHESTRATION_IQ'),
    preferredQuote: ?PreferredQuoteModelType,
    availableQuoteIds: Array<string>,
    sourceOrderId: string,
    lineItems: Array<LineItemModelType>
};

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class TemperatureThreshold extends ImmutableModel {
    static fromJS(json: TemperatureThresholdModelType): TemperatureThreshold {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): TemperatureThresholdModelType {
        return {
            lowerThreshold: this.lowerThreshold,
            higherThreshold: this.higherThreshold,
            temperatureType: this.temperatureType,
            unit: this.unit
        };
    }

    get lowerThreshold(): number {
        return this._state.get('lowerThreshold');
    }

    setLowerThreshold(lowerThreshold: number): this {
        return this.clone(this._state.set('lowerThreshold', lowerThreshold));
    }

    get higherThreshold(): number {
        return this._state.get('higherThreshold');
    }

    setHigherThreshold(higherThreshold: number): this {
        return this.clone(this._state.set('higherThreshold', higherThreshold));
    }

    get temperatureType(): TemperatureType {
        return this._state.get('temperatureType');
    }

    setTemperatureType(temperatureType: TemperatureType): this {
        return this.clone(this._state.set('temperatureType', temperatureType));
    }

    get unit(): TemperatureUnitType {
        return this._state.get('unit');
    }

    setUnit(unit: TemperatureUnitType): this {
        return this.clone(this._state.set('unit', unit));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class MasterOrder extends ImmutableModel {
    static fromJS(json: MasterOrderModelType): MasterOrder {
        const state: Object = Object.assign({}, json);
        state.customProperties = Immutable.Map(state.customProperties);
        state.auditMetadata = state.auditMetadata
            ? AuditMetadata.fromJS(state.auditMetadata)
            : state.auditMetadata;
        state.team = TeamId.fromJS(state.team);
        state.skills = Immutable.List(state.skills);
        state.temperatureThreshold = state.temperatureThreshold
            ? TemperatureThreshold.fromJS(state.temperatureThreshold)
            : state.temperatureThreshold;
        state.pickupContactPoint = state.pickupContactPoint
            ? PickupContactPoint.fromJS(state.pickupContactPoint)
            : state.pickupContactPoint;
        state.pickupLocationAddress = LocationAddress.fromJS(
            state.pickupLocationAddress
        );
        state.pickupLatLng = LatLng.fromJS(state.pickupLatLng);
        state.pickupSlot = TimeSlot.fromJS(state.pickupSlot);
        state.pickupSlots = Immutable.List(state.pickupSlots).map(item =>
            TimeSlot.fromJS(item)
        );
        state.pickupAmount = AmountTransaction.fromJS(state.pickupAmount);
        state.dropContactPoint = state.dropContactPoint
            ? PickupContactPoint.fromJS(state.dropContactPoint)
            : state.dropContactPoint;
        state.dropLocationAddress = LocationAddress.fromJS(
            state.dropLocationAddress
        );
        state.dropLatLng = LatLng.fromJS(state.dropLatLng);
        state.dropSlot = TimeSlot.fromJS(state.dropSlot);
        state.dropSlots = Immutable.List(state.dropSlots).map(item =>
            TimeSlot.fromJS(item)
        );
        state.dropAmount = AmountTransaction.fromJS(state.dropAmount);
        state.volume = Volume.fromJS(state.volume);
        state.weight = Weight.fromJS(state.weight);
        state.quantity = state.quantity
            ? Quantity.fromJS(state.quantity)
            : state.quantity;
        state.preferredQuote = state.preferredQuote
            ? PreferredQuote.fromJS(state.preferredQuote)
            : state.preferredQuote;
        state.availableQuoteIds = Immutable.List(state.availableQuoteIds);
        state.lineItems = Immutable.List(state.lineItems).map(item =>
            LineItem.fromJS(item)
        );
        return new this(Immutable.Map(state));
    }

    toJS(): MasterOrderModelType {
        return {
            clientId: this.clientId,
            id: this.id,
            code: this.code,
            name: this.name,
            status: this.status,
            orderStatus: this.orderStatus,
            effectiveStatus: this.effectiveStatus,
            version: this.version,
            batchId: this.batchId,
            planId: this.planId,
            tourId: this.tourId,
            shiftId: this.shiftId,
            scanId: this.scanId,
            sequence: this.sequence,
            userId: this.userId,
            homebaseId: this.homebaseId,
            customProperties: this.customProperties.toObject(),
            auditMetadata: this.auditMetadata
                ? this.auditMetadata.toJS()
                : this.auditMetadata,
            team: this.team.toJS(),
            teamId: this.teamId,
            skills: this.skills.toArray(),
            orderedOn: this.orderedOn,
            temperatureThreshold: this.temperatureThreshold
                ? this.temperatureThreshold.toJS()
                : this.temperatureThreshold,
            pickupVisitName: this.pickupVisitName,
            pickupLocationId: this.pickupLocationId,
            pickupContactPoint: this.pickupContactPoint
                ? this.pickupContactPoint.toJS()
                : this.pickupContactPoint,
            pickupLocationAddress: this.pickupLocationAddress.toJS(),
            pickupLatLng: this.pickupLatLng.toJS(),
            pickupDate: this.pickupDate,
            pickupSlot: this.pickupSlot.toJS(),
            pickupSlots: this.pickupSlots.toArray().map(item => item.toJS()),
            pickupTransactionDuration: this.pickupTransactionDuration,
            pickupAmount: this.pickupAmount.toJS(),
            pickupCustomerId: this.pickupCustomerId,
            pickupAddressId: this.pickupAddressId,
            dropVisitName: this.dropVisitName,
            dropLocationId: this.dropLocationId,
            dropContactPoint: this.dropContactPoint
                ? this.dropContactPoint.toJS()
                : this.dropContactPoint,
            dropLocationAddress: this.dropLocationAddress.toJS(),
            dropLatLng: this.dropLatLng.toJS(),
            dropDate: this.dropDate,
            dropSlot: this.dropSlot.toJS(),
            dropSlots: this.dropSlots.toArray().map(item => item.toJS()),
            dropTransactionDuration: this.dropTransactionDuration,
            dropAmount: this.dropAmount.toJS(),
            dropCustomerId: this.dropCustomerId,
            dropAddressId: this.dropAddressId,
            volume: this.volume.toJS(),
            weight: this.weight.toJS(),
            quantity: this.quantity ? this.quantity.toJS() : this.quantity,
            usecase: this.usecase,
            preferredQuote: this.preferredQuote
                ? this.preferredQuote.toJS()
                : this.preferredQuote,
            availableQuoteIds: this.availableQuoteIds.toArray(),
            sourceOrderId: this.sourceOrderId,
            lineItems: this.lineItems.toArray().map(item => item.toJS())
        };
    }

    get clientId(): string {
        return this._state.get('clientId');
    }

    setClientId(clientId: string): this {
        return this.clone(this._state.set('clientId', clientId));
    }

    get id(): string {
        return this._state.get('id');
    }

    setId(id: string): this {
        return this.clone(this._state.set('id', id));
    }

    get code(): string {
        return this._state.get('code');
    }

    setCode(code: string): this {
        return this.clone(this._state.set('code', code));
    }

    get name(): string {
        return this._state.get('name');
    }

    setName(name: string): this {
        return this.clone(this._state.set('name', name));
    }

    get status(): EntityStatusEnum {
        return this._state.get('status');
    }

    setStatus(status: EntityStatusEnum): this {
        return this.clone(this._state.set('status', status));
    }

    get orderStatus(): OrderStatusEnum {
        return this._state.get('orderStatus');
    }

    setOrderStatus(orderStatus: OrderStatusEnum): this {
        return this.clone(this._state.set('orderStatus', orderStatus));
    }

    get effectiveStatus(): EffectiveStatusEnum {
        return this._state.get('effectiveStatus');
    }

    setEffectiveStatus(effectiveStatus: EffectiveStatusEnum): this {
        return this.clone(this._state.set('effectiveStatus', effectiveStatus));
    }

    get version(): number {
        return this._state.get('version');
    }

    setVersion(version: number): this {
        return this.clone(this._state.set('version', version));
    }

    get batchId(): string {
        return this._state.get('batchId');
    }

    setBatchId(batchId: string): this {
        return this.clone(this._state.set('batchId', batchId));
    }

    get planId(): string {
        return this._state.get('planId');
    }

    setPlanId(planId: string): this {
        return this.clone(this._state.set('planId', planId));
    }

    get tourId(): string {
        return this._state.get('tourId');
    }

    setTourId(tourId: string): this {
        return this.clone(this._state.set('tourId', tourId));
    }

    get shiftId(): string {
        return this._state.get('shiftId');
    }

    setShiftId(shiftId: string): this {
        return this.clone(this._state.set('shiftId', shiftId));
    }

    get scanId(): string {
        return this._state.get('scanId');
    }

    setScanId(scanId: string): this {
        return this.clone(this._state.set('scanId', scanId));
    }

    get sequence(): number {
        return this._state.get('sequence');
    }

    setSequence(sequence: number): this {
        return this.clone(this._state.set('sequence', sequence));
    }

    get userId(): string {
        return this._state.get('userId');
    }

    setUserId(userId: string): this {
        return this.clone(this._state.set('userId', userId));
    }

    get homebaseId(): string {
        return this._state.get('homebaseId');
    }

    setHomebaseId(homebaseId: string): this {
        return this.clone(this._state.set('homebaseId', homebaseId));
    }

    get customProperties(): Immutable.Map<string, string> {
        return this._state.get('customProperties');
    }

    setCustomProperties(customProperties: Immutable.Map<string, string>): this {
        return this.clone(
            this._state.set('customProperties', customProperties)
        );
    }

    get auditMetadata(): ?AuditMetadata {
        return this._state.get('auditMetadata');
    }

    setAuditMetadata(auditMetadata: ?AuditMetadata): this {
        return this.clone(this._state.set('auditMetadata', auditMetadata));
    }

    get team(): TeamId {
        return this._state.get('team');
    }

    setTeam(team: TeamId): this {
        return this.clone(this._state.set('team', team));
    }

    get teamId(): string {
        return this._state.get('teamId');
    }

    setTeamId(teamId: string): this {
        return this.clone(this._state.set('teamId', teamId));
    }

    get skills(): Immutable.List<string> {
        return this._state.get('skills');
    }

    setSkills(skills: Immutable.List<string>): this {
        return this.clone(this._state.set('skills', skills));
    }

    get orderedOn(): string {
        return this._state.get('orderedOn');
    }

    setOrderedOn(orderedOn: string): this {
        return this.clone(this._state.set('orderedOn', orderedOn));
    }

    get temperatureThreshold(): ?TemperatureThreshold {
        return this._state.get('temperatureThreshold');
    }

    setTemperatureThreshold(temperatureThreshold: ?TemperatureThreshold): this {
        return this.clone(
            this._state.set('temperatureThreshold', temperatureThreshold)
        );
    }

    get pickupVisitName(): string {
        return this._state.get('pickupVisitName');
    }

    setPickupVisitName(pickupVisitName: string): this {
        return this.clone(this._state.set('pickupVisitName', pickupVisitName));
    }

    get pickupLocationId(): string {
        return this._state.get('pickupLocationId');
    }

    setPickupLocationId(pickupLocationId: string): this {
        return this.clone(
            this._state.set('pickupLocationId', pickupLocationId)
        );
    }

    get pickupContactPoint(): ?PickupContactPoint {
        return this._state.get('pickupContactPoint');
    }

    setPickupContactPoint(pickupContactPoint: ?PickupContactPoint): this {
        return this.clone(
            this._state.set('pickupContactPoint', pickupContactPoint)
        );
    }

    get pickupLocationAddress(): LocationAddress {
        return this._state.get('pickupLocationAddress');
    }

    setPickupLocationAddress(pickupLocationAddress: LocationAddress): this {
        return this.clone(
            this._state.set('pickupLocationAddress', pickupLocationAddress)
        );
    }

    get pickupLatLng(): LatLng {
        return this._state.get('pickupLatLng');
    }

    setPickupLatLng(pickupLatLng: LatLng): this {
        return this.clone(this._state.set('pickupLatLng', pickupLatLng));
    }

    get pickupDate(): string {
        return this._state.get('pickupDate');
    }

    setPickupDate(pickupDate: string): this {
        return this.clone(this._state.set('pickupDate', pickupDate));
    }

    get pickupSlot(): TimeSlot {
        return this._state.get('pickupSlot');
    }

    setPickupSlot(pickupSlot: TimeSlot): this {
        return this.clone(this._state.set('pickupSlot', pickupSlot));
    }

    get pickupSlots(): Immutable.List<TimeSlot> {
        return this._state.get('pickupSlots');
    }

    setPickupSlots(pickupSlots: Immutable.List<TimeSlot>): this {
        return this.clone(this._state.set('pickupSlots', pickupSlots));
    }

    get pickupTransactionDuration(): number {
        return this._state.get('pickupTransactionDuration');
    }

    setPickupTransactionDuration(pickupTransactionDuration: number): this {
        return this.clone(
            this._state.set(
                'pickupTransactionDuration',
                pickupTransactionDuration
            )
        );
    }

    get pickupAmount(): AmountTransaction {
        return this._state.get('pickupAmount');
    }

    setPickupAmount(pickupAmount: AmountTransaction): this {
        return this.clone(this._state.set('pickupAmount', pickupAmount));
    }

    get pickupCustomerId(): string {
        return this._state.get('pickupCustomerId');
    }

    setPickupCustomerId(pickupCustomerId: string): this {
        return this.clone(
            this._state.set('pickupCustomerId', pickupCustomerId)
        );
    }

    get pickupAddressId(): string {
        return this._state.get('pickupAddressId');
    }

    setPickupAddressId(pickupAddressId: string): this {
        return this.clone(this._state.set('pickupAddressId', pickupAddressId));
    }

    get dropVisitName(): string {
        return this._state.get('dropVisitName');
    }

    setDropVisitName(dropVisitName: string): this {
        return this.clone(this._state.set('dropVisitName', dropVisitName));
    }

    get dropLocationId(): string {
        return this._state.get('dropLocationId');
    }

    setDropLocationId(dropLocationId: string): this {
        return this.clone(this._state.set('dropLocationId', dropLocationId));
    }

    get dropContactPoint(): ?PickupContactPoint {
        return this._state.get('dropContactPoint');
    }

    setDropContactPoint(dropContactPoint: ?PickupContactPoint): this {
        return this.clone(
            this._state.set('dropContactPoint', dropContactPoint)
        );
    }

    get dropLocationAddress(): LocationAddress {
        return this._state.get('dropLocationAddress');
    }

    setDropLocationAddress(dropLocationAddress: LocationAddress): this {
        return this.clone(
            this._state.set('dropLocationAddress', dropLocationAddress)
        );
    }

    get dropLatLng(): LatLng {
        return this._state.get('dropLatLng');
    }

    setDropLatLng(dropLatLng: LatLng): this {
        return this.clone(this._state.set('dropLatLng', dropLatLng));
    }

    get dropDate(): string {
        return this._state.get('dropDate');
    }

    setDropDate(dropDate: string): this {
        return this.clone(this._state.set('dropDate', dropDate));
    }

    get dropSlot(): TimeSlot {
        return this._state.get('dropSlot');
    }

    setDropSlot(dropSlot: TimeSlot): this {
        return this.clone(this._state.set('dropSlot', dropSlot));
    }

    get dropSlots(): Immutable.List<TimeSlot> {
        return this._state.get('dropSlots');
    }

    setDropSlots(dropSlots: Immutable.List<TimeSlot>): this {
        return this.clone(this._state.set('dropSlots', dropSlots));
    }

    get dropTransactionDuration(): number {
        return this._state.get('dropTransactionDuration');
    }

    setDropTransactionDuration(dropTransactionDuration: number): this {
        return this.clone(
            this._state.set('dropTransactionDuration', dropTransactionDuration)
        );
    }

    get dropAmount(): AmountTransaction {
        return this._state.get('dropAmount');
    }

    setDropAmount(dropAmount: AmountTransaction): this {
        return this.clone(this._state.set('dropAmount', dropAmount));
    }

    get dropCustomerId(): string {
        return this._state.get('dropCustomerId');
    }

    setDropCustomerId(dropCustomerId: string): this {
        return this.clone(this._state.set('dropCustomerId', dropCustomerId));
    }

    get dropAddressId(): string {
        return this._state.get('dropAddressId');
    }

    setDropAddressId(dropAddressId: string): this {
        return this.clone(this._state.set('dropAddressId', dropAddressId));
    }

    get volume(): Volume {
        return this._state.get('volume');
    }

    setVolume(volume: Volume): this {
        return this.clone(this._state.set('volume', volume));
    }

    get weight(): Weight {
        return this._state.get('weight');
    }

    setWeight(weight: Weight): this {
        return this.clone(this._state.set('weight', weight));
    }

    get quantity(): ?Quantity {
        return this._state.get('quantity');
    }

    setQuantity(quantity: ?Quantity): this {
        return this.clone(this._state.set('quantity', quantity));
    }

    get usecase(): ?('DISPATCH_IQ' | 'ORCHESTRATION_IQ') {
        return this._state.get('usecase');
    }

    setUsecase(usecase: ?('DISPATCH_IQ' | 'ORCHESTRATION_IQ')): this {
        return this.clone(this._state.set('usecase', usecase));
    }

    get preferredQuote(): ?PreferredQuote {
        return this._state.get('preferredQuote');
    }

    setPreferredQuote(preferredQuote: ?PreferredQuote): this {
        return this.clone(this._state.set('preferredQuote', preferredQuote));
    }

    get availableQuoteIds(): Immutable.List<string> {
        return this._state.get('availableQuoteIds');
    }

    setAvailableQuoteIds(availableQuoteIds: Immutable.List<string>): this {
        return this.clone(
            this._state.set('availableQuoteIds', availableQuoteIds)
        );
    }

    get sourceOrderId(): string {
        return this._state.get('sourceOrderId');
    }

    setSourceOrderId(sourceOrderId: string): this {
        return this.clone(this._state.set('sourceOrderId', sourceOrderId));
    }

    get lineItems(): Immutable.List<LineItem> {
        return this._state.get('lineItems');
    }

    setLineItems(lineItems: Immutable.List<LineItem>): this {
        return this.clone(this._state.set('lineItems', lineItems));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class PreferredQuote extends ImmutableModel {
    static fromJS(json: PreferredQuoteModelType): PreferredQuote {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): PreferredQuoteModelType {
        return {
            carrier: this.carrier,
            totalFee: this.totalFee,
            expiresAt: this.expiresAt
        };
    }

    get carrier(): string {
        return this._state.get('carrier');
    }

    setCarrier(carrier: string): this {
        return this.clone(this._state.set('carrier', carrier));
    }

    get totalFee(): {
        amount: number,
        currency: string,
        estimatedDropOffTime: ?string
    } {
        return this._state.get('totalFee');
    }

    setTotalFee(totalFee: {
        amount: number,
        currency: string,
        estimatedDropOffTime: ?string
    }): this {
        return this.clone(this._state.set('totalFee', totalFee));
    }

    get expiresAt(): string {
        return this._state.get('expiresAt');
    }

    setExpiresAt(expiresAt: string): this {
        return this.clone(this._state.set('expiresAt', expiresAt));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class LineItem extends ImmutableModel {
    static fromJS(json: LineItemModelType): LineItem {
        const state: Object = Object.assign({}, json);
        state.parts = Immutable.List(state.parts).map(item =>
            LineItemPart.fromJS(item)
        );
        state.price = state.price ? Amount.fromJS(state.price) : state.price;
        return new this(Immutable.Map(state));
    }

    toJS(): LineItemModelType {
        return {
            id: this.id,
            lineItemId: this.lineItemId,
            name: this.name,
            parts: this.parts.toArray().map(item => item.toJS()),
            price: this.price ? this.price.toJS() : this.price,
            quantity: this.quantity,
            quantityUnit: this.quantityUnit
        };
    }

    get id(): string {
        return this._state.get('id');
    }

    setId(id: string): this {
        return this.clone(this._state.set('id', id));
    }

    get lineItemId(): string {
        return this._state.get('lineItemId');
    }

    setLineItemId(lineItemId: string): this {
        return this.clone(this._state.set('lineItemId', lineItemId));
    }

    get name(): string {
        return this._state.get('name');
    }

    setName(name: string): this {
        return this.clone(this._state.set('name', name));
    }

    get parts(): Immutable.List<LineItemPart> {
        return this._state.get('parts');
    }

    setParts(parts: Immutable.List<LineItemPart>): this {
        return this.clone(this._state.set('parts', parts));
    }

    get price(): ?Amount {
        return this._state.get('price');
    }

    setPrice(price: ?Amount): this {
        return this.clone(this._state.set('price', price));
    }

    get quantity(): number {
        return this._state.get('quantity');
    }

    setQuantity(quantity: number): this {
        return this.clone(this._state.set('quantity', quantity));
    }

    get quantityUnit(): ?string {
        return this._state.get('quantityUnit');
    }

    setQuantityUnit(quantityUnit: ?string): this {
        return this.clone(this._state.set('quantityUnit', quantityUnit));
    }
}
